import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { useTranslation } from "react-i18next";
import TextareaAutosize from "react-textarea-autosize";

interface EarlyAccessDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  value: string;
  onValueChange: (value: string) => void;
  loading: boolean;
  success: boolean;
  onSubmit: () => void;
  onClose: () => void;
}

export const EarlyAccessDialog = ({
  open,
  onOpenChange,
  value,
  onValueChange,
  loading,
  success,
  onSubmit,
  onClose,
}: EarlyAccessDialogProps) => {
  const { t } = useTranslation();

  return (
    <Dialog
      open={open}
      onOpenChange={(openState) => {
        onOpenChange(openState);
        if (!openState) {
          onClose();
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("team.integrations.earlyAccess.title")}</DialogTitle>
          <DialogDescription>
            {t("team.integrations.earlyAccess.description")}
          </DialogDescription>
        </DialogHeader>
        {success ? (
          <div className="py-4 text-green-700">
            {t("team.integrations.earlyAccess.success")}
          </div>
        ) : (
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="early-access">
                {t("team.integrations.earlyAccess.label")}
              </Label>
              <TextareaAutosize
                id="early-access"
                value={value}
                onChange={(e) => onValueChange(e.target.value)}
                placeholder={t("team.integrations.earlyAccess.placeholder")}
                minRows={4}
                className="w-full px-3 py-2 border rounded-md border-primary/20 bg-primary/5 text-foreground placeholder:text-secondary/50 focus:outline-hidden focus:ring-2 focus:ring-primary/50 dark:bg-primary/10 dark:border-primary/30"
              />
            </div>
          </div>
        )}
        <DialogFooter>
          {success ? (
            <Button type="button" variant="default" onClick={onClose}>
              {t("team.integrations.ok")}
            </Button>
          ) : (
            <>
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={loading}
              >
                {t("team.integrations.earlyAccess.cancel")}
              </Button>
              <Button
                type="button"
                onClick={onSubmit}
                disabled={!value.trim() || loading}
              >
                {loading ? (
                  <span className="flex items-center justify-center">
                    <span className="mr-2 loader" />
                    {t("team.integrations.earlyAccess.sending")}
                  </span>
                ) : (
                  t("team.integrations.earlyAccess.button")
                )}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
